import styled from '@emotion/styled';
import { useAddOddToBasketMutation } from '@features/basket/api/basketApi';
import { IAddToBasket } from '@features/basket/api/basketApi.type';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { grey } from '@mui/material/colors';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useState } from 'react';
import Countdown, { zeroPad } from 'react-countdown';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import { useFetchRecommendedExpressQuery } from './recommendedExpressApi';

const ROLL_MAX = 2147483647;
const URGENT_MS = 60_000;

export const createRoll = () => Math.floor(Math.random() * ROLL_MAX) + 1;

const StyledDrawer = styled(SwipeableDrawer)`
  .MuiPaper-root {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    max-height: 90vh;
    overflow: auto;
  }

  .puller-container {
    display: flex;
    padding: 8px;

    .puller {
      margin: 0 auto;
      width: 50px;
      height: 6px;
      background-color: ${grey[700]};
      border-radius: 3px;
    }
  }

  .rec-express-leg {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 2px 12px;
    padding: 8px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
  }

  .rec-express-total {
    display: flex;
    justify-content: space-between;
    padding-top: 8px;
    border-top: 2px solid ${(props) => props.theme.palette.warning.main};
    font-weight: 700;
  }
`;

export interface IRecommendedExpressSheetProps {
  open: boolean;
  onClose: () => void;
}

export default function RecommendedExpressSheet({ open, onClose }: IRecommendedExpressSheetProps) {
  const [ exclude, setExclude ] = useState<number[]>([]);
  const [ roll, setRoll ] = useState(createRoll);
  const [ adding, setAdding ] = useState(false);
  const { data, isFetching } = useFetchRecommendedExpressQuery({ exclude, roll }, { skip: !open });
  const [ addToBasket ] = useAddOddToBasketMutation();

  useEffect(() => {
    if (open) {
      setExclude([]);
      setRoll(createRoll());
    }
  }, [ open ]);

  const handleRefresh = useCallback(() => {
    if (data) {
      setExclude(data.legs.map((leg) => leg.eventId));
    }
    setRoll(createRoll());
  }, [ data ]);

  const handleSwap = useCallback((eventId: number) => {
    setExclude((prev) => (prev.includes(eventId) ? prev : [ ...prev, eventId ]));
    setRoll(createRoll());
  }, []);

  const handleAdd = useCallback(async () => {
    if (!data || adding) {
      return;
    }
    setAdding(true);
    for (const leg of data.legs) {
      const payload: IAddToBasket = {
        eventId: leg.eventId,
        outcomeItemId: leg.outcomeItemId,
        outcomeSportId: leg.outcomeSportId,
        value: leg.value,
      };
      if (leg.info?.length) {
        payload.info = [ ...leg.info ];
      }
      await addToBasket(payload).unwrap().catch(() => undefined);
    }
    setAdding(false);
    onClose();
  }, [ addToBasket, adding, data, onClose ]);

  const busy = isFetching || adding;
  const legs = data?.legs ?? [];
  const sports = data?.personalized && data.basedOn?.length ? data.basedOn.map((sport) => sport.name).join(', ') : '';

  return (
    <StyledDrawer
      open={open}
      anchor="bottom"
      onOpen={() => undefined}
      onClose={onClose}
      variant="temporary"
      disableSwipeToOpen
      ModalProps={{ keepMounted: false }}
      className="rec-express-sheet"
    >
      <Box className="puller-container"><Box className="puller"/></Box>
      <Box px={2} pb={2} display="flex" flexDirection="column" gap={1}>
        <Typography component="h4" fontSize={17} fontWeight={700}>
          <FormattedMessage id="RECOMMENDED_EXPRESS_TITLE"/>
        </Typography>
        {!data && (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress color="warning" size={32}/></Box>
        )}
        {data && !legs.length && (
          <Typography className="rec-express-empty" color="text.secondary" py={3} textAlign="center">
            <FormattedMessage id="RECOMMENDED_EXPRESS_EMPTY"/>
          </Typography>
        )}
        {data && legs.length > 0 && (
          <Box display="flex" flexDirection="column" gap={1} sx={{ opacity: isFetching ? 0.6 : 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
              <Typography fontSize={12} color="text.secondary">
                {sports && <FormattedMessage id="RECOMMENDED_EXPRESS_BASED_ON" values={{ sports }}/>}
              </Typography>
              <Button size="small" color="warning" variant="outlined" disabled={busy} onClick={handleRefresh} className="rec-express-refresh">
                <FormattedMessage id="RECOMMENDED_EXPRESS_REFRESH"/>
              </Button>
            </Box>
            {data.validUntil ? (
              <Typography fontSize={12} color="text.secondary" className="rec-express-valid">
                <FormattedMessage id="RECOMMENDED_EXPRESS_VALID"/>{' '}
                <Countdown
                  key={data.validUntil}
                  daysInHours
                  date={data.validUntil * 1000}
                  onComplete={() => setRoll(createRoll())}
                  renderer={({ total, hours, minutes, seconds }) => (
                    <Typography component="span" fontSize={12} fontWeight={700} color={total <= URGENT_MS ? 'error.main' : 'text.primary'} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {zeroPad(hours)}:{zeroPad(minutes)}:{zeroPad(seconds)}
                    </Typography>
                  )}
                />
              </Typography>
            ) : null}
            {legs.map((leg) => (
              <Box className="rec-express-leg" key={`${leg.eventId}-${leg.outcomeSportId}-${leg.outcomeItemId}`}>
                <Typography fontSize={14}>
                  {leg.host}{leg.guest ? ` - ${leg.guest}` : ''}
                  <br/>
                  <b>{leg.outcomeLabel}</b>
                </Typography>
                <Typography fontSize={15} fontWeight={700} color="warning.main" textAlign="right">
                  {Number(leg.odd).toFixed(2)}
                </Typography>
                <Typography fontSize={11} color="text.secondary">
                  {leg.sportName}{' · '}
                  <FormattedDate value={leg.startsAt * 1000} day="2-digit" month="2-digit"/>{' '}
                  <FormattedTime value={leg.startsAt * 1000}/>
                </Typography>
                <Button size="small" color="info" disabled={busy} onClick={() => handleSwap(leg.eventId)} className="rec-express-swap" sx={{ minWidth: 0, p: 0, justifySelf: 'end' }}>
                  <FormattedMessage id="RECOMMENDED_EXPRESS_SWAP"/>
                </Button>
              </Box>
            ))}
            <Box className="rec-express-total">
              <span><FormattedMessage id="RECOMMENDED_EXPRESS_COMBINED"/></span>
              <span>{data.combinedOdd !== undefined ? Number(data.combinedOdd).toFixed(2) : '—'}</span>
            </Box>
            <Button fullWidth size="large" color="warning" variant="contained" disabled={busy} onClick={handleAdd} className="rec-express-add">
              <FormattedMessage id="RECOMMENDED_EXPRESS_ADD"/>
            </Button>
          </Box>
        )}
      </Box>
    </StyledDrawer>
  );
}
